import { DefaultOptionType } from 'antd/lib/select'
import isEmpty from 'lodash/isEmpty'
import uniqueId from 'lodash/uniqueId'

import { ConfigItem } from 'Core/interfaces'
import { entityToStringLabelValue, NameCodeToLabelValue } from 'Core/utils'
import { StateProvince } from 'Core/utils/storeHelpers/types'

import { Audit, Kpi, Location, State } from 'Portals/Customer/entities'

import { getMonthes, getQuarters, MONTHES_INDEXED, QUARTERS_SHORTLY } from '../../constants'

export const stateToLabelValue = (state: State): LabelValue => ({
	label: `${state.stateName}, ${state.stateAbbreviation} (${state.countryCode})`,
	value: state.id.toString()
})

export const mouSubjectEntityToLabelValue = (entity: {
	id: number
	mouNumber: string
	subject: string
}): LabelValue<number> => {
	return {
		label: [entity.subject, entity.mouNumber].filter((item) => item).join(' - '),
		value: entity.id
	}
}

export const mouNumberEntityToLabelValue = (entity: {
	id: number
	mouNumber: string
	subject: string
}): LabelValue<number> => {
	return {
		label: [entity.mouNumber, entity.subject].filter((item) => item).join(' - '),
		value: entity.id
	}
}

export const formatOptionsCity = (
	options: string[],
	config: ConfigItem<null>
): DefaultOptionType[] => {
	if (isEmpty(options)) {
		return []
	}

	return options.map((value, index) => {
		const { filterType, apiSearchFieldName, UrlParam, UrlParamLabel } = config

		return {
			label: value,
			value,
			key: `${config.label}-${value}-${index}`,
			props: {
				filterType,
				apiSearchFieldName,
				value,
				label: value,
				UrlParam,
				UrlParamLabel
			}
		}
	})
}

export const disposalFacilityNameEntityToLabelValue = (entity: {
	disposalFacilityName: string
	documentNumber: string
	manifestId: number
}): LabelValue<number> => {
	return {
		label: [entity.disposalFacilityName, entity.documentNumber]
			.filter((item) => item)
			.join(' - '),
		value: entity.manifestId
	}
}

export const manifestDocumentNumbersEntityToLabelValue = (entity: {
	disposalFacilityName: string
	documentNumber: string
	manifestId: number
}): LabelValue<number> => {
	return {
		label: [entity.documentNumber, entity.disposalFacilityName]
			.filter((item) => item)
			.join(' - '),
		value: entity.manifestId
	}
}

export const powerBiConfigToLabelValue = (entity: {
	description: string
	id: number
}): LabelValue<number> => ({
	label: entity.description,
	value: entity.id
})

const DEFAULT_KPI_QUARTER_COUNT = 5
const DEFAULT_KPI_MONTH_COUNT = 16

export const formatKpiLocationFilterOptions = (entity: Location.FilterOptions[]) =>
	entity.map(({ location, categories }) => ({
		label: `${location.companyserviceSiteId} - ${location.name}`,
		value: location.id.toString(),
		categoriesOptions: categories.map(NameCodeToLabelValue)
	}))

export const formatKpiMetricFilterOptions = (
	entity: Kpi.Metric.FilterOptions[]
): Kpi.Metric.FormattedFilterOptions[] =>
	entity.map(({ category, kpis }) => ({
		label: category.name,
		value: category.id,
		kpiOptions: kpis.map((kpi) => entityToStringLabelValue({ name: kpi.name, id: kpi.id }))
	}))

export const locationToLabelValueString = (entity: Location.LocationFilter) => ({
	label: `${entity.companyserviceSiteId} - ${entity.name}`,
	value: entity.id.toString()
})

export const getLastMonthNamesWithYearForProvidedMonthCount = (
	months: number = DEFAULT_KPI_MONTH_COUNT
) => {
	const now = new Date()
	let month = now.getMonth()
	let year = now.getFullYear()
	const MONTHES = getMonthes()
	// do not change
	const names = [
		MONTHES.JAN,
		MONTHES.FEB,
		MONTHES.MAR,
		MONTHES.APR,
		MONTHES.MAY,
		MONTHES.JUN,
		MONTHES.JUL,
		MONTHES.AUG,
		MONTHES.SEP,
		MONTHES.OCT,
		MONTHES.NOV,
		MONTHES.DEC
	]

	const res: ExpandedLabelValue[] = []
	for (let i = 0; i < months; ++i) {
		const date = {
			label: year + ' ' + names[month],
			value: `${MONTHES_INDEXED[month]} + ${year}`,
			saveValue: MONTHES_INDEXED[month],
			extra: year,
			key: uniqueId()
		}
		res.push(date)
		if (--month === -1) {
			month = 11
			--year
		}
	}
	return res
}

export const getLastQuaterNamesForProvidedQuaterCount = (
	quaters: number = DEFAULT_KPI_QUARTER_COUNT
) => {
	const now = new Date()
	const month = now.getMonth()
	let year = now.getFullYear()
	let quarter = Math.floor((month + 3) / 3)
	const QUARTERS = getQuarters()
	// do not change
	const values: { [key: number]: LabelValue } = {
		1: { value: QUARTERS_SHORTLY['QTR-1'], label: QUARTERS['QTR-1'] },
		2: { value: QUARTERS_SHORTLY['QTR-2'], label: QUARTERS['QTR-2'] },
		3: { value: QUARTERS_SHORTLY['QTR-3'], label: QUARTERS['QTR-3'] },
		4: { value: QUARTERS_SHORTLY['QTR-4'], label: QUARTERS['QTR-4'] }
	}

	const res: ExpandedLabelValue[] = []
	for (let i = 0; i < quaters; ++i) {
		const date = {
			value: `${values[quarter].value} + ${year}`,
			label: `${year} ${values[quarter].label}`,
			saveValue: values[quarter].value,
			extra: year,
			key: uniqueId()
		}
		res.push(date)
		if (--quarter === 0) {
			quarter = 4
			--year
		}
	}
	return res
}

export const getLastYearsForProvidedMonthCount = (months: number = DEFAULT_KPI_MONTH_COUNT) => {
	const now = new Date()
	let month = now.getMonth()
	let year = now.getFullYear()

	const res: ExpandedLabelValue[] = []
	for (let i = 0; i < months; ++i) {
		const val = `${year}`
		const date = {
			label: val,
			value: val,
			saveValue: val,
			extra: year,
			key: uniqueId()
		}
		if (!res.find((item) => item.value === date.value)) {
			res.push(date)
		}

		if (--month === -1) {
			month = 11
			--year
		}
	}
	return res
}

export const stateOptionToLabelValue = (entity: StateProvince) => ({
	label: `${entity.name}, ${entity.code} (${entity.countryCode})`,
	value: entity.id
})

export const createdUserToLabelValue = (entity: Audit.Search.EntityWithUser) => ({
	label: `${entity.userId ? entity.userId : entity.id} - ${entity.name}`,
	value: entity.userId ? entity.userId : entity.id
})
